import React from 'react';
import clsx from 'clsx';
import Breadcrumbs from '@components/molecules/products/breadcrumbs/breadcrumbs';
import { Breadcrumb } from '@interfaces/models/breadcrumb';
import Link from '@components/atoms/link/link';
import Spinner from '@components/atoms/spinner/spinner';
import styles from './page-header.module.scss';

export interface PageHeaderCTA {
  label: string;
  href: string;
  onClick?: () => void;
  isLoading?: boolean;
  dataCy?: string;
  dataAttributes?: Record<string, string>;
}

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  banner?: React.ReactNode;
  breadcrumbs?: Breadcrumb[];
  showDivider?: boolean;
  className?: string;
  subtitleClassName?: string;
  dataCyPrefix?: string;
  cta?: PageHeaderCTA;
  isAlertHeader?: boolean;
}

const PageHeader: React.FC<PageHeaderProps> = (props) => {
  const {
    title,
    subtitle,
    banner,
    breadcrumbs,
    showDivider = false,
    className,
    subtitleClassName,
    dataCyPrefix,
    cta,
    isAlertHeader = false,
  } = props;

  const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 0;
  const shouldShowDivider = showDivider && !cta?.isLoading && !isAlertHeader;

  return (
    <div
      className={clsx(
        styles.pageHeader,
        shouldShowDivider && styles['pageHeader--withDivider'],
        className,
        isAlertHeader && styles['pageHeader--alertHeader'],
        !isAlertHeader && 'innerContainer',
      )}
      data-cy={dataCyPrefix ? `${dataCyPrefix}_header` : undefined}
    >
      {banner && (
        <div
          className={styles.pageHeader__banner}
          data-cy={dataCyPrefix ? `${dataCyPrefix}_banner` : undefined}
        >
          {banner}
        </div>
      )}

      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs
          breadcrumbs={breadcrumbs}
          isCatalogPage={false}
          customClass={styles.pageHeader__breadcrumbs}
          dataCyIdentifier={dataCyPrefix ? `breadcrumbs_${dataCyPrefix}` : undefined}
        />
      )}

      <h1
        className={clsx(styles.pageHeader__title, !hasBreadcrumbs && styles['pageHeader__title--noBreadcrumbs'])}
        data-cy={dataCyPrefix ? `${dataCyPrefix}_title` : undefined}
      >
        {title}
      </h1>

      {cta && (
        <div className={styles.pageHeader__cta}>
          {cta.isLoading ? (
            <Spinner className={styles.pageHeader__cta__spinner} />
          ) : (
            <Link
              href={cta.href}
              onClick={cta.onClick}
              className={styles.pageHeader__cta__link}
              data-cy={cta.dataCy}
              {...cta.dataAttributes}
            >
              {cta.label}
            </Link>
          )}
        </div>
      )}

      {subtitle && (
        <p
          className={clsx(styles.pageHeader__subtitle, subtitleClassName)}
          data-cy={dataCyPrefix ? `${dataCyPrefix}_subtitle` : undefined}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
};

export default PageHeader;
